import { useState } from 'react';
import { Link } from 'react-router-dom';
import TopAppBar, { AccountActionButton } from '../components/TopAppBar';
import Icon from '../components/Icon';
import { initialsOf, useAccount } from '../auth/AccountContext';
import { signOut } from '../auth/authApi';

/**
 * Account view — Obsidian Companion layout for the authenticated user's
 * profile, subscription, sync/security, and destructive actions.
 *
 * The shell already fetched `/account` via `AccountProvider`, so this view
 * reads from `useAccount()` instead of re-fetching. A local refresh button
 * re-runs the context fetch. `signOut()` clears the server session cookie
 * (best-effort) then we hard-navigate to /auth.
 *
 * `AccountUser` exposes no `created_at` / renewal timestamps, so those
 * fields render `—` rather than fabricated dates (rule 7).
 */
const ACCOUNT_FEATURES: readonly string[] = [
  'Voice typing and AI writing features reflect your current plan',
  'Review available history in the web companion',
  'Manage snippets when transcript sync is enabled',
];

export default function AccountView() {
  const { user, loading, error, refresh } = useAccount();
  const [signingOut, setSigningOut] = useState(false);

  async function handleSignOut() {
    if (signingOut) return;
    setSigningOut(true);
    await signOut();
    window.location.assign('/auth');
  }

  return (
    <>
      <TopAppBar
        title="Account"
        backTo="/app/settings"
        breadcrumb={[
          { label: 'Settings', to: '/app/settings' },
          { label: 'Account' },
        ]}
        actions={<AccountActionButton />}
      />

      <div className="mx-auto w-full max-w-3xl px-gutter pb-24 pt-24">
        <p className="mb-6 font-body-md text-body-md text-on-surface-variant">
          Manage your profile and subscription
        </p>

        {error && (
          <div className="mb-6 flex items-center justify-between gap-4 rounded-xl border border-outline-variant/50 bg-error-container/20 px-4 py-3">
            <span className="font-body-sm text-body-sm text-error">{error}</span>
            <button
              type="button"
              onClick={() => void refresh()}
              className="min-h-11 min-w-11 rounded-lg px-3 font-label-lg text-label-lg text-primary hover:underline"
            >
              Retry
            </button>
          </div>
        )}

        {loading && !user && (
          <div className="flex flex-col items-center gap-3 py-16 text-on-surface-variant">
            <Icon name="progress_activity" className="animate-spin text-[32px] text-primary" />
            <span className="font-body-md text-body-md">Loading account…</span>
          </div>
        )}

        {user && (
          <div className="flex flex-col gap-6">
            {/* Profile */}
            <section className="flex flex-col items-start justify-between gap-5 rounded-xl bg-surface-container-low p-5 ring-1 ring-outline-variant/35 md:flex-row md:items-center">
              <div className="flex items-center gap-6">
                <div className="relative flex h-20 w-20 flex-shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-primary/20 bg-surface-container-high">
                  {user.picture ? (
                    <img
                      src={user.picture}
                      alt={user.name}
                      referrerPolicy="no-referrer"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <span className="font-display-md text-display-md font-bold text-primary">
                      {initialsOf(user.name)}
                    </span>
                  )}
                </div>
                <div className="min-w-0">
                  <h2 className="truncate font-headline-lg text-headline-lg font-bold text-on-surface">
                    {user.name}
                  </h2>
                  <p className="mt-1 truncate font-body-md text-body-md text-on-surface-variant">
                    {user.email}
                  </p>
                  <p className="mt-2 font-label-sm text-label-sm uppercase tracking-wider text-outline">
                    Joined —
                  </p>
                </div>
              </div>
              <span className="rounded-full bg-surface-container px-3 py-1.5 font-label-sm text-label-sm text-on-surface-variant">
                Google account
              </span>
            </section>

            {/* Subscription */}
            <section className="relative overflow-hidden rounded-xl bg-surface-container-low p-5 ring-1 ring-outline-variant/35">
              <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-primary/10 blur-3xl" />
              <div className="mb-6 flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
                <div>
                  <div className="mb-2 flex items-center gap-3">
                    <h3 className="font-title-md text-title-md font-bold text-on-surface">
                      {user.is_pro ? 'Loaf Pro' : 'Free plan'}
                    </h3>
                    {user.is_pro && (
                      <span className="rounded-full border border-primary/20 bg-primary/10 px-3 py-1 font-label-sm text-label-sm font-bold uppercase tracking-wider text-primary">
                        Active
                      </span>
                    )}
                  </div>
                  <p className="font-body-sm text-body-sm text-on-surface-variant">
                    {user.is_pro ? 'Pro access active' : 'Upgrade for additional AI writing features'}
                  </p>
                </div>
                <Link
                  to="/app/plans"
                  className="flex min-h-11 w-full items-center justify-center rounded-lg bg-primary px-5 font-label-lg text-label-lg font-bold text-on-primary transition-colors hover:bg-primary/90 active:scale-95 md:w-auto"
                >
                  {user.is_pro ? 'Manage Plan' : 'Upgrade to Pro'}
                </Link>
              </div>
              <div className="rounded-lg bg-surface-container p-4 ring-1 ring-outline-variant/25">
                <h4 className="mb-4 font-label-sm text-label-sm uppercase tracking-wider text-on-surface-variant">
                  Account features
                </h4>
                <ul className="space-y-3">
                  {ACCOUNT_FEATURES.map((perk) => (
                    <li key={perk} className="flex items-center gap-3">
                      <Icon name="check_circle" className="text-[20px] text-primary" />
                      <span className="font-body-md text-body-md text-on-surface">{perk}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            {/* Sync & Security */}
            <section className="flex flex-col overflow-hidden rounded-xl bg-surface-container ring-1 ring-outline-variant/30">
              <div className="flex items-center justify-between gap-4 border-b border-outline-variant/30 px-4 py-3">
                <div className="flex items-center gap-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-surface text-primary">
                    <Icon name="devices" />
                  </div>
                  <div className="flex flex-col">
                    <span className="font-body-lg text-body-lg font-medium text-on-surface">
                      Companion sync
                    </span>
                    <span className="font-body-sm text-body-sm text-on-surface-variant">
                      Linked to the Loaf mobile app
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="flex items-center gap-2 font-label-sm text-label-sm uppercase tracking-wider text-primary">
                    <span className="h-2 w-2 animate-pulse rounded-full bg-primary" />
                    {user.transcript_sync_enabled ? 'Active' : 'Paused'}
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between gap-4 px-4 py-3">
                <div className="flex items-center gap-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-surface text-on-surface-variant">
                    <Icon name="lock" />
                  </div>
                  <div className="flex flex-col">
                    <span className="font-body-lg text-body-lg font-medium text-on-surface">
                      Password
                    </span>
                    <span className="font-body-sm text-body-sm text-on-surface-variant">
                      Google sign-in — no password
                    </span>
                  </div>
                </div>
              </div>
            </section>

            {/* Destructive */}
            <section className="mt-4 flex flex-col overflow-hidden rounded-xl bg-surface-container ring-1 ring-outline-variant/30">
              <button
                type="button"
                onClick={handleSignOut}
                disabled={signingOut}
                className="group flex items-center justify-between border-b border-outline-variant/30 p-4 text-left transition-colors hover:bg-surface-container-high disabled:opacity-60"
              >
                <div className="flex items-center gap-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-surface text-on-surface-variant">
                    <Icon name="logout" />
                  </div>
                  <span className="font-body-lg text-body-lg font-medium text-on-surface">
                    {signingOut ? 'Signing out…' : 'Sign Out'}
                  </span>
                </div>
              </button>
              <Link
                to="/account/delete"
                className="group flex items-center justify-between p-4 transition-colors hover:bg-error-container/20"
              >
                <div className="flex items-center gap-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-surface text-error transition-colors group-hover:bg-error-container/30">
                    <Icon name="delete_forever" />
                  </div>
                  <span className="font-body-lg text-body-lg font-medium text-error">
                    Delete Account
                  </span>
                </div>
              </Link>
            </section>
          </div>
        )}
      </div>
    </>
  );
}
